import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { setCanonical, setMeta } from '@/utils/seo';
import { BreadcrumbSchema, SpeakableSchema, FAQSchema } from '@/components/SEOSchemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AllCitiesPage() {
  const [cities, setCities] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setCanonical('/all-cities');
    setMeta('All Philippine Cities | LegaSecure Guide', 'Browse estate planning guides for 150+ Philippine cities. Find SPA, guardian mandate, and digital vault information in your city.');
    axios.get(`${API}/cities?limit=500`).then(r => setCities(r.data.cities || [])).catch(() => {});
  }, []);

  const filtered = cities.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.region.toLowerCase().includes(search.toLowerCase()));
  const grouped = filtered.reduce((acc, c) => { acc[c.region] = acc[c.region] || []; acc[c.region].push(c); return acc; }, {});

  return (
    <div data-testid="all-cities-page">
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'All Cities' }]} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/80"></div>
        </div>
        <div className="ls-container relative z-10 py-12">
          <h1 className="text-3xl md:text-4xl font-bold font-['Crimson_Pro'] mb-3" style={{ color: '#1B365D' }}>Estate Planning in Philippine Cities</h1>
          <p className="speakable-intro text-lg mb-6" style={{ color: '#4A5568' }}>Browse estate planning guides for {cities.length}+ cities across the Philippines.</p>
          <input
            data-testid="city-search" aria-label="Search Philippine cities"
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search cities..."
            className="w-full max-w-md px-4 py-3 rounded-xl bg-white text-[var(--ls-text)] placeholder-[var(--ls-text-light)] border border-[var(--ls-border)] focus:outline-none focus:border-[var(--ls-gold)]"
          />
        </div>
      </section>

      <section className="ls-section">
        <div className="ls-container">
          {Object.entries(grouped).sort().map(([region, regionCities]) => (
            <div key={region} className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[var(--ls-navy)]">{region}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {regionCities.map(c => (
                  <Link key={c.slug} to={`/city/${c.slug}`} className="ls-card p-4 flex items-center gap-3 hover:border-[var(--ls-gold)] border border-transparent">
                    <i className="fa-solid fa-city text-[var(--ls-gold)]"></i>
                    <div>
                      <p className="font-semibold text-sm">{c.name}</p>
                      <p className="text-xs text-[var(--ls-text-light)]">{c.province}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function AllCountriesPage() {
  const [countries, setCountries] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setCanonical('/all-countries');
    setMeta('OFW Country Guides | LegaSecure Guide', 'Estate planning guides for Filipinos in 50+ countries. Find SPA, guardian mandate, and legacy protection help for OFWs worldwide.');
    axios.get(`${API}/countries?limit=500`).then(r => setCountries(r.data.countries || [])).catch(() => {});
  }, []);

  const filtered = countries.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.region.toLowerCase().includes(search.toLowerCase()));
  const grouped = filtered.reduce((acc, c) => { acc[c.region] = acc[c.region] || []; acc[c.region].push(c); return acc; }, {});

  return (
    <div data-testid="all-countries-page">
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'OFW Countries' }]} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/80"></div>
        </div>
        <div className="ls-container relative z-10 py-12">
          <h1 className="text-3xl md:text-4xl font-bold font-['Crimson_Pro'] mb-3" style={{ color: '#1B365D' }}>OFW Country Guides</h1>
          <p className="speakable-intro text-lg mb-6" style={{ color: '#4A5568' }}>Estate planning guides for Filipinos in {countries.length}+ countries worldwide.</p>
          <input
            data-testid="country-search" aria-label="Search OFW countries"
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search countries..."
            className="w-full max-w-md px-4 py-3 rounded-xl bg-white text-[var(--ls-text)] placeholder-[var(--ls-text-light)] border border-[var(--ls-border)] focus:outline-none focus:border-[var(--ls-gold)]"
          />
        </div>
      </section>

      <section className="ls-section">
        <div className="ls-container">
          {Object.entries(grouped).sort().map(([region, regionCountries]) => (
            <div key={region} className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[var(--ls-navy)]">{region}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {regionCountries.map(c => (
                  <Link key={c.slug} to={`/ofw/${c.slug}`} className="ls-card p-4 flex items-center gap-3 hover:border-[var(--ls-gold)] border border-transparent">
                    <i className="fa-solid fa-globe text-[var(--ls-gold)]"></i>
                    <div>
                      <p className="font-semibold text-sm">{c.name}</p>
                      <p className="text-xs text-[var(--ls-text-light)]">{c.ofw_estimate} OFWs</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
